import { store } from '@/services/store.service'
import { NostrEvent } from '@nostr/tools/wasm'
import { atom, getDefaultStore, useAtomValue } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { withAtomEffect } from 'jotai-effect'

export const pendingIdsAtom = atomWithStorage<string[]>('s:pending', [], undefined, {
  getOnInit: true
})

// events currently in the local store corresponding to pendingIdsAtom; auto-
// refetched whenever the id list changes.
export const pendingEventsAtom = withAtomEffect(atom<NostrEvent[]>([]), (get, set) => {
  const ids = get(pendingIdsAtom)
  if (ids.length === 0) {
    set(pendingEventsAtom, [])
    return
  }
  let cancelled = false
  store.queryEvents({ ids }, 5000).then((events) => {
    if (!cancelled) set(pendingEventsAtom, events)
  })
  return () => {
    cancelled = true
  }
})

const storej = getDefaultStore()

export async function savePendingEvent(event: NostrEvent) {
  await store.saveEvent(event)
  storej.set(pendingIdsAtom, (prev) => {
    if (prev.includes(event.id)) return prev
    return [event.id, ...prev]
  })
}

// the event got published somewhere, so it's no longer pending, but we keep it in the store
export function unmarkPendingEvent(eventId: string) {
  storej.set(pendingIdsAtom, (prev) =>
    prev.includes(eventId) ? prev.filter((id) => id !== eventId) : prev
  )
}

// the event was thrown away, so remove it from the store too
export async function discardPendingEvent(eventId: string) {
  try {
    await store.deleteEvents([eventId])
  } finally {
    unmarkPendingEvent(eventId)
  }
}

// drop ids whose events are no longer in the store (otherwise they'd count as pending
// forever without ever showing up in the list). only done once on startup so we don't
// race with in-flight saves and deletes.
async function pruneOrphanedPendingIds() {
  const ids = storej.get(pendingIdsAtom)
  if (ids.length === 0) return
  try {
    const found = new Set((await store.queryEvents({ ids }, 5000)).map((evt) => evt.id))
    storej.set(pendingIdsAtom, (prev) => prev.filter((id) => found.has(id) || !ids.includes(id)))
  } catch (err) {
    console.warn('failed to check pending events:', err)
  }
}
pruneOrphanedPendingIds()

// hook helpers
export function usePendingIds() {
  return useAtomValue(pendingIdsAtom)
}

export function usePendingEvents() {
  return useAtomValue(pendingEventsAtom)
}
